import ApiService from '@/services/api.service';
import TokenService from '@/services/token.service';
import { mutations } from '@/store/store';
import { ParticipantDTO } from '@/models/ParticipantDTO';
import { MedicalDataDTO } from '@/models/MedicalDataDTO';
import { RecapDTO } from '@/models/RecapDTO';
import { IdentificationDTO } from '@/models/IdentificationDTO';
import { PUBLIC_ROOT_API } from '@/config/app.config';
import LocalStoreService from '@/store/local.store';
import { AxiosResponse } from 'axios';

export default class ParticipantService {

  /**
   * Login the participant
   *
   * @returns {IdentificationDTO} A DTO object containing su route prefix
   */
  public static async login(partId: number, dateOfBirth: Date, suRoutePrefix: string) {
    const requestData = {
      method: 'post',
      url: `${PUBLIC_ROOT_API}/participants/identify`,
      data: {
        partId,
        dateOfBirth,
        suRoutePrefix,
      },
    };

    return await ApiService.post(requestData.url, requestData.data)
      .then((response: any) => {
        const identficationData = response.data as IdentificationDTO;
        return identficationData;
      });
  }

  /**
   * Open a participant dossier
   * @param partId The participant ID
   * @param dateOfBirth The participant date of birth
   */
  public static async openDossier(partId: number, dateOfBirth: Date): Promise<IdentificationDTO> {
    const requestData = {
      method: 'post',
      url: `${PUBLIC_ROOT_API}/participants/open`,
      data: {
        partId,
        dateOfBirth,
      },
    };

    return await ApiService.post(requestData.url, requestData.data)
      // tslint:disable-next-line: no-empty
      .then((response: any) => {
        mutations.setIsDossierOpened(true);
        LocalStoreService.setItem(LocalStoreService.localItemKeys.dossierId, partId.toString());
        return response.data as IdentificationDTO;
      });
  }

  /**
   * Logout the current participant by calling the API in order to expire the cookies.
   * It also removes the JWT token from storage and XSRF token from header.
   */
  public static async logout() {
    const requestData = {
      url: `${PUBLIC_ROOT_API}/Participants/logout`,
    };

    await ApiService.post(requestData.url, {})
      .then((response: any) => {
        // Remove the token and remove Authorization header from Api Service as well
        this.logoutFromFrontend();
      });
  }

  /**
   * Logout the current participant by removing the JWT token from storage and XSRF token from header.
   */
  public static logoutFromFrontend() {
    TokenService.removeToken();
    ApiService.removeHeader(TokenService.X_XSRF_TOKEN);
    mutations.setIsIdentified(false);
    LocalStoreService.deleteItem(LocalStoreService.localItemKeys.inclusionType);
  }

  /**
   * Fetch the administrative data from PublicInclusionContent if any.
   * Otherwise fetch the actual administrative data.
   *
   * @returns {ParticipantDTO} A DTO object representing the participant
   */
  public static async fetchParticipant(partId: number) {
    return await ApiService.get(`${PUBLIC_ROOT_API}/participants/${partId}/contents/adminData`)
      .then((response: any) => {
        return response.data as ParticipantDTO;
      });
  }

  /**
   * Fetch the contact data
   *
   * @returns {QueueParam} A DTO object representing the participant's contact
   */
  public static async fetchIdentificationData(partId: number) {
    return await ApiService.get(`${PUBLIC_ROOT_API}/participants/${partId}/contents/identification`)
      .then((response: any) => {
        return response.data as IdentificationDTO;
      });
  }

  /**
   * Update administrative data in PublicInclusionContent
   * @param {ParticipantDTO} participant The participant DTO object
   */
  public static async updateAdministrativeInfo(participant: ParticipantDTO) {
    await ApiService.post(`${PUBLIC_ROOT_API}/participants/${participant.partId}/administrative`,
      participant)
      // tslint:disable-next-line: no-empty
      .then((response: any) => { });
  }

  /**
   * Update identification data in PublicInclusionContent
   * @param {ParticipantDTO} participant The participant DTO object
   */
  public static async updateIdentificationInfos(partId: number, identification: IdentificationDTO) {
    await ApiService.post(`${PUBLIC_ROOT_API}/participants/${partId}/identification`,
      identification)
      // tslint:disable-next-line: no-empty
      .then((response: any) => { });
  }

  /**
   * Save the medical data (doctor, insurance, etc.)
   * @param medicalData The medial data to save.
   */
  public static async saveMedicalData(medicalData: MedicalDataDTO) {
    await ApiService.post(`${PUBLIC_ROOT_API}/participants/${medicalData.partId}/medical`,
      medicalData)
      // tslint:disable-next-line: no-empty
      .then((response: any) => { });
  }

  /**
   * Fetch the medical data from PublicInclusionContent if any
   * @param partId The participant ID
   * @returns {MedicalDataDTO} A DTO object representing the medical data
   */
  public static async fetchMedicalDataContent(partId: number): Promise<MedicalDataDTO> {
    return await ApiService.get(`${PUBLIC_ROOT_API}/participants/${partId}/contents/medicalData`)
      .then((response: any) => {
        return response.data as MedicalDataDTO;
      });
  }

  /**
   * Fetch all data from PublicInclusionContent (participant data, medical data, questions)
   * @param partId The participant ID
   * @returns {RecapDTO} A DTO object representing the all data from PublicInclusionContent
   * (i.e. ParticipantDTO, MedicalDataDTO, QuestionDTO)
   */
  public static async fetchPublicInclusionContent(partId: number): Promise<RecapDTO> {
    return await ApiService.get(`${PUBLIC_ROOT_API}/participants/${partId}/contents`)
      .then((response: any) => {
        return response.data as RecapDTO;
      });
  }

  /**
   * Sends the questionnaire to the queue
   * @param partId The participant ID
   */
  public static async sendToQueue(partId: number) {
    await ApiService.post(
      `${PUBLIC_ROOT_API}/participants/${partId}/sendToQueue`, {})
      // tslint:disable-next-line: no-empty
      .then((response: any) => { });
  }

  /**
   * Sends the questionnaire to the queue
   * @param partId The participant ID
   */
  public static async hasParticipantInclusionAdvice(partId: number): Promise<boolean | undefined> {
    return await ApiService.get(
      `${PUBLIC_ROOT_API}/participants/${partId}/advice`)
      .then((response: AxiosResponse) => {
        return response.data as boolean;
      })
      .catch(() => {
        return undefined;
      });

  }
}
